import { useState } from "react";
import {
  Paper,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Stack,
  Text,
} from "@mantine/core";
import { IconX, IconPencil, IconCheck } from "@tabler/icons-react";
import { QuestionItem } from "./QuestionItem";
import { BuilderQuestionGroup, BuilderQuestion } from "@/types/interview.types";

interface QuestionGroupProps {
  group: BuilderQuestionGroup;
  onChange: (group: BuilderQuestionGroup) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export function QuestionGroup({
  group,
  onChange,
  onRemove,
  canRemove,
}: QuestionGroupProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);

  const handleSaveName = () => {
    onChange({ ...group, name: editName.trim() || group.name });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditName(group.name);
    setIsEditing(false);
  };

  const handleAddQuestion = () => {
    const newQuestion: BuilderQuestion = {
      id: crypto.randomUUID(),
      groupId: group.id,
      type: "open-ended",
      text: "",
      maxFollowups: 1,
    };
    onChange({
      ...group,
      questions: [...group.questions, newQuestion],
    });
  };

  const handleQuestionChange = (
    questionId: string,
    updates: Partial<BuilderQuestion>,
  ) => {
    onChange({
      ...group,
      questions: group.questions.map((q) =>
        q.id === questionId ? { ...q, ...updates } : q,
      ),
    });
  };

  const handleRemoveQuestion = (questionId: string) => {
    onChange({
      ...group,
      questions: group.questions.filter((q) => q.id !== questionId),
    });
  };

  return (
    <Paper className="question-group-section" p="md" radius="md">
      <div className="question-group-header">
        {isEditing ? (
          <Group gap="sm" className="question-group-heading-edit">
            <TextInput
              value={editName}
              onChange={(e) => setEditName(e.currentTarget.value)}
              placeholder="e.g., Job Satisfaction, Work Environment"
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName();
                if (e.key === "Escape") handleCancelEdit();
              }}
              autoFocus
            />
            <ActionIcon
              variant="filled"
              color="violet"
              onClick={handleSaveName}
            >
              <IconCheck size={16} />
            </ActionIcon>
            <Button variant="outline" size="xs" onClick={handleAddQuestion}>
              + Add question
            </Button>
            {canRemove && (
              <ActionIcon variant="outline" color="red" onClick={onRemove}>
                <IconX size={16} />
              </ActionIcon>
            )}
          </Group>
        ) : (
          <Group gap="sm" className="question-group-heading-view">
            <Text fw={600} size="lg" className="question-group-heading">
              <span className="question-group-heading-text">{group.name}</span>
              <ActionIcon
                variant="subtle"
                color="violet"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="edit-group-name-btn"
              >
                <IconPencil size={14} />
              </ActionIcon>
            </Text>
            <Button variant="outline" size="xs" onClick={handleAddQuestion}>
              + Add question
            </Button>
            {canRemove && (
              <ActionIcon
                variant="outline"
                color="red"
                onClick={onRemove}
                className="remove-group-btn"
              >
                <IconX size={16} />
              </ActionIcon>
            )}
          </Group>
        )}
      </div>

      <Stack gap="sm" className="questions-list" mt="md">
        {group.questions.map((question) => (
          <QuestionItem
            key={question.id}
            question={question}
            onChange={(updates) => handleQuestionChange(question.id, updates)}
            onRemove={() => handleRemoveQuestion(question.id)}
          />
        ))}
        {group.questions.length === 0 && (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No questions yet. Click "+ Add question" to add one.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
